'use client'

import { SUBSCRIBE_TICKETS_TAB, SubscribesTicketPaymentType } from '@entities/subscribes/model/subscribes-tickets-model'
import { checkSubscribeUrlAccess } from '@entities/subscribes/utils/check-subscribe-url-access'
import { getFinalPaymentType } from '@entities/subscribes/utils/get-payment-type-priority'
import { isPaybackAccessible, LECTURE_TICKETS_QUERY_PARAMS, parsePaybackFlag } from '@features/payment-type'
import {
  CurriculumType,
  curriculumTypeSchema,
  Destination,
  destinationSchema,
  paymentTypeSchema,
  promotionTypeSchema,
} from '@server/schemas'
import { FEATURE_FLAG_KEYS } from '@shared/constants'
import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

import { useFlagsCompat } from '@podo/flags/react'

const PAYMENT_TYPE_DESTINATION_MAP: Partial<Record<SubscribesTicketPaymentType, Destination>> = {
  SUBSCRIBE: destinationSchema.Enum.SUBSCRIBE_TICKET_LIST,
  LUMP_SUM: destinationSchema.Enum.LUMPSUM_TICKET_LIST,
  PAYBACK: destinationSchema.Enum.LUMPSUM_TICKET_LIST,
  IPAD: destinationSchema.Enum.IPAD_TICKET_LIST,
  EXTEND: destinationSchema.Enum.SUBSCRIBE_TICKET_LIST,
}

type UseSubscribeTicketDestinationResult = {
  finalPaymentType: SubscribesTicketPaymentType
  loginDestination: Destination
}

export const useSubscribeTicketDestination = (curriculumType?: CurriculumType): UseSubscribeTicketDestinationResult => {
  const search = useSearchParams()
  const searchParamsString = search.toString()

  const { subscribe_tickets_url_enabled: subscribeTicketsUrlEnabledFlag, payback_tickets: paybackFlagState } =
    useFlagsCompat([FEATURE_FLAG_KEYS.SUBSCRIBE_TICKETS_URL_ENABLED, FEATURE_FLAG_KEYS.PAYBACK_TICKETS])

  const subscribeTicketsUrlEnabled = subscribeTicketsUrlEnabledFlag?.enabled ?? false
  const subscribeTicketsUrlValue = subscribeTicketsUrlEnabledFlag?.value
  const paybackFlagValue = paybackFlagState?.value

  const { finalPaymentType, isPaybackPromotion } = useMemo<{
    finalPaymentType: SubscribesTicketPaymentType
    isPaybackPromotion: boolean
  }>(() => {
    const parsedSearch = new URLSearchParams(searchParamsString)

    const isSubscribeUrlAccessEnabled = checkSubscribeUrlAccess({
      enabled: subscribeTicketsUrlEnabled,
      value: subscribeTicketsUrlValue,
    })

    const isPaybackUrlAccessEnabled = isPaybackAccessible(parsePaybackFlag(paybackFlagValue))

    const rawPaymentType = parsedSearch.get(LECTURE_TICKETS_QUERY_PARAMS.paymentType)

    // 페이백 탭은 LUMP_SUM의 결제 방식을 사용함
    const normalizedPaymentTypeParam = rawPaymentType
      ? ((rawPaymentType === SUBSCRIBE_TICKETS_TAB.PAYBACK
          ? SUBSCRIBE_TICKETS_TAB.LUMP_SUM
          : rawPaymentType) as SubscribesTicketPaymentType)
      : undefined

    const isSubscribeEnabledByUrl =
      parsedSearch.get(LECTURE_TICKETS_QUERY_PARAMS.enable) === paymentTypeSchema.Enum.SUBSCRIBE &&
      isSubscribeUrlAccessEnabled
    const isPaybackEnabledByUrl =
      parsedSearch.get(LECTURE_TICKETS_QUERY_PARAMS.enable) === SUBSCRIBE_TICKETS_TAB.PAYBACK &&
      isPaybackUrlAccessEnabled

    const baseFinalPaymentType = getFinalPaymentType({
      paymentTypeParam: normalizedPaymentTypeParam,
      isSubscribeEnabledByUrl,
      subscribeTicketsEnabled: true,
      isPaybackEnabledByUrl,
    })

    const isPaybackPromotion =
      parsedSearch.get(LECTURE_TICKETS_QUERY_PARAMS.promotionType) === promotionTypeSchema.Enum.PAYBACK

    return {
      finalPaymentType: baseFinalPaymentType,
      isPaybackPromotion,
    }
  }, [searchParamsString, subscribeTicketsUrlEnabled, subscribeTicketsUrlValue, paybackFlagValue])

  const loginDestination = useMemo<Destination>(() => {
    if (curriculumType === curriculumTypeSchema.Enum.SMART_TALK) {
      return destinationSchema.Enum.SMART_TALK_TICKET_LIST
    }
    if (isPaybackPromotion) {
      return destinationSchema.Enum.PAYBACK_TICKET_LIST
    }

    return PAYMENT_TYPE_DESTINATION_MAP[finalPaymentType] ?? destinationSchema.Enum.SUBSCRIBE_TICKET_LIST
  }, [curriculumType, finalPaymentType, isPaybackPromotion])

  return {
    finalPaymentType,
    loginDestination,
  }
}
